import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDown } from 'lucide-react';
import { useTranslations } from '../hooks/useTranslations';

const LanguageSelector = ({ showText = false, compact = false, isScrolled = true }) => {
  const { currentLanguage, languages, changeLanguage, loading } = useTranslations();
  const [isOpen, setIsOpen] = useState(false);

  const currentLang = languages.find(lang => lang.code === currentLanguage);

  const handleLanguageChange = async (languageCode) => {
    await changeLanguage(languageCode);
    setIsOpen(false);
  };

  if (loading || !currentLang) {
    return (
      <div className="relative">
        <button 
          className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
            compact ? 'text-sm' : 'text-base'
          } text-gray-600 hover:text-blue-600 transition-colors`}
          disabled
        >
          <Globe className={compact ? "h-4 w-4" : "h-5 w-5"} />
          {showText && <span>Language</span>}
        </button>
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center space-x-2 px-3 py-2 rounded-lg ${
          compact ? 'text-sm' : 'text-base'
        } transition-colors ${
          isScrolled 
            ? 'text-gray-700 hover:text-blue-600 hover:bg-blue-50' 
            : 'text-white hover:text-blue-300 hover:bg-white/10'
        }`}
        aria-label="Select Language"
      >
        <Globe className={compact ? "h-4 w-4" : "h-5 w-5"} />
        {showText && (
          <>
            <span className="hidden sm:inline">{currentLang.native_name}</span>
            <span className="sm:hidden">{currentLang.code.toUpperCase()}</span>
          </>
        )}
        {!compact && (
          <>
            <span className="text-lg">{currentLang.flag}</span>
            <ChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            {/* Backdrop */}
            <div 
              className="fixed inset-0 z-10" 
              onClick={() => setIsOpen(false)}
            />
            
            {/* Dropdown */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-20"
            >
              <div className="px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-100">
                Select Language
              </div>
              
              <div className="max-h-64 overflow-y-auto">
                {languages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => handleLanguageChange(language.code)}
                    className={`w-full flex items-center space-x-3 px-4 py-2 text-left transition-colors ${
                      currentLanguage === language.code
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <span className="text-xl">{language.flag}</span>
                    <div className="flex-1">
                      <div className="font-medium">{language.native_name}</div>
                      <div className="text-sm text-gray-500">{language.name}</div>
                    </div>
                    {currentLanguage === language.code && (
                      <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                    )}
                  </button>
                ))}
              </div>
              
              <div className="px-3 py-2 text-xs text-gray-500 border-t border-gray-100 mt-2">
                Powered by AI Translation
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;